import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Row, Col, Button, Divider, Typography, Alert } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import { RcFile } from 'antd/es/upload';
import { Attachments } from 'types';
import { isLocaleEnglish } from 'utils/languageUtils';
import { DocumentsCardPep } from './DocumentsCardPep';
import { DocumentsCardForCIConfirm } from './DocumentsCardForCIConfirm';
import {
  PostClaimCIData,
  HospitalItem,
  FamilyMemberItem,
  OtherHospitalItem,
  OtherClaimItem,
  useRelationshiplist,
} from 'all-in-one-shared-api';
import { useKeycloakReady } from 'hooks';
import { EnquirySuccessPage } from './otp-verification/OtpVerificationSuccessPage';
import { useUploadFilesForCI } from 'api/fileManager';
import { FileUploaded } from 'components/form';
import { formatCurrency } from 'utils/currency';
import { DocumentLink } from 'components/documentLink/DocumentLink';
import styled from 'styled-components';
import { getFilename } from 'utils/getFilename';
import GreetingSection from 'components/navbar/GreetingSection';
import { mergeCachedFiles } from '../claimFilesCache';

const { Text } = Typography;

const LinkText = styled(Text)`
  cursor: pointer;
  color: var(--home-news-promotion-link-color);
`;
const ClaimPageContainer = styled.div`
  margin: 0;
`;
type ClaimFormInput = PostClaimCIData & {
  referenceNo?: string;
  bpmReferenceNo?: string;
  changeStatus?: string;
};

interface LocationState {
  formData: ClaimFormInput;
  updateFiles?: 'normal' | 'update' | 'view';
}
export interface ClaimDocuments {
  receipts?: FileUploaded[];
  referralLetters?: FileUploaded[];
  otherDocuments?: FileUploaded[];
  prevClaimSettlements?: FileUploaded[];
}

// Custom confirm field component matching PaymentConfrimCard style
const ConfirmField: React.FC<{
  label?: React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ label, children, style }) => (
  <div
    style={{
      border: 'none',
      borderBottom: '1px solid #CCCCCC',
      backgroundColor: 'transparent',
      fontSize: '20px',
      fontFamily: 'Roboto400',
      marginBottom: '10px',
      padding: '4px 0',
      ...style,
    }}
  >
    {label && (
      <div
        style={{
          color: '#6f7072',
          fontWeight: 'normal',
          fontSize: '17px',
          fontFamily: 'Roboto500',
          marginBottom: '4px',
        }}
      >
        {label}
      </div>
    )}
    <div
      style={{
        color: '#20252b',
        fontSize: '20px',
        fontFamily: 'Roboto400',
        wordBreak: 'break-word',
      }}
    >
      {children}
    </div>
  </div>
);

interface FileLikeObject {
  uid?: string;
  name?: string;
  fileName?: string;
  url?: string;
}

const extractFileName = (path: string): string => {
  return path.split('/').pop() || path;
};

const ANT_FILE_UID_PREFIX = 'rc-upload';

// 本地文件链接组件：通过 originFileObj 创建 blob URL 预览
const LocalFileLink = ({ file }: { file: UploadFile }) => {
  const { t } = useTranslation();
  const handleClick = () => {
    if (file.originFileObj) {
      const url = URL.createObjectURL(file.originFileObj as Blob);
      window.open(url, '_blank');
    }
  };
  return <LinkText onClick={handleClick}>{file.name || t('common.unknownFile')}</LinkText>;
};

// 判断是否为本地文件（normal 流程传入的上传文件，uid 以 rc-upload 开头且有 originFileObj）
const isLocalFile = (file: unknown): file is UploadFile => {
  if (typeof file !== 'object' || file === null) return false;
  const fileObj = file as FileLikeObject;
  return !!(fileObj.uid && fileObj.uid.startsWith(ANT_FILE_UID_PREFIX) && (file as UploadFile).originFileObj);
};

const DocumentDisplayField = ({ label, attachments }: { label: string; attachments?: Attachments | unknown[] }) => {
  const fileList = Array.isArray(attachments)
    ? (attachments as unknown[])
    : (attachments as Attachments)?.fileList || [];

  if (!fileList || fileList.length === 0) {
    return null;
  }

  // 提取服务端文件的路径字符串
  const getFilePath = (file: unknown): string => {
    if (typeof file === 'string') return file;
    if (typeof file === 'object' && file !== null) {
      const fileObj = file as FileLikeObject;
      return fileObj.uid || fileObj.url || '';
    }
    return '';
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '4px 8px',
        marginBottom: '10px',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          color: '#6f7072',
          fontWeight: 'normal',
          fontSize: '17px',
          fontFamily: 'Roboto500',
          marginBottom: '8px',
          wordBreak: 'break-word',
          width: '100%',
        }}
      >
        {label}
      </div>
      <div
        style={{
          width: '100%',
          wordBreak: 'break-word',
        }}
      >
        {fileList.map((file) => {
          // 本地文件（normal 流程）：用 originFileObj 的 blob URL 预览
          if (isLocalFile(file)) {
            return (
              <div key={file.uid} style={{ marginBottom: '4px' }}>
                <LocalFileLink file={file} />
              </div>
            );
          }
          // 服务端文件（update/view 流程）：用 DocumentLink 调用 API 获取 presigned URL
          const filePath = getFilePath(file);
          if (!filePath) return null;
          return (
            <div key={filePath} style={{ marginBottom: '4px' }}>
              <DocumentLink filePath={filePath} documentType="CI" key={filePath} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ClaimConfirmPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state as LocationState) ?? { formData: {} as ClaimFormInput };
  const formData: ClaimFormInput = useMemo(
    () => mergeCachedFiles(locationState.formData || ({} as ClaimFormInput)),
    [locationState.formData],
  );
  const { updateFiles = 'normal' } = locationState;
  const isEnglish = isLocaleEnglish(i18n.language);
  const [randoms, setRandoms] = useState(Math.random());
  // 将服务端文件数据转换为 UploadFile 格式
  // 参考 EditClaimForm 的 buildUploadFileList 逻辑：
  //   - 字符串类型：uid 保留完整路径（用于区分服务端文件/本地新文件），name 取文件名
  //   - 对象类型：保留原对象的 uid/name/url
  const convertToUploadFiles = (files: unknown[]): UploadFile[] => {
    return files.map((file, index) => {
      if (typeof file === 'string') {
        return {
          uid: file, // 保留完整路径作为 uid，用于后续判断是否为服务端文件
          name: getFilename(file) || extractFileName(file),
          status: 'done' as const,
        };
      }
      if (typeof file === 'object' && file !== null) {
        const fileObj = file as FileLikeObject;
        // 保留原始 uid（非 rc-upload 前缀即为服务端文件），不重新生成
        const uid = fileObj.uid || fileObj.url || `file-${index}`;
        return {
          ...(file as UploadFile),
          uid,
          name:
            fileObj.name ||
            fileObj.fileName ||
            getFilename(fileObj.url || '') ||
            extractFileName(fileObj.url || '') ||
            `file-${index}`,
          status: 'done' as const,
        };
      }
      return {
        uid: `file-${index}`,
        name: `file-${index}`,
        status: 'done' as const,
      };
    });
  };
  // 存储上传的文件数据（不加入formData）
  const [pepAttachments, setPepAttachments] = useState<UploadFile[]>([]);
  const [documentInvalidPep, setDocumentInvalidPep] = useState(false);
  const [documentInvalidCI, setDocumentInvalidCI] = useState(false);
  const documentsSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (updateFiles === 'update') {
      setTimeout(() => {
        documentsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [updateFiles]);

  // 初始文件列表（从服务器获取的旧文件）- 转换为 UploadFile 格式
  const initialClaimFormPartIIFiles = useMemo(
    () => convertToUploadFiles((formData?.criticalIllnessClaimFormAttachments as unknown[]) || []),
    [formData?.criticalIllnessClaimFormAttachments],
  );
  const initialIdentityProofFiles = useMemo(
    () => convertToUploadFiles((formData?.proofOfIdentityAttachments as unknown[]) || []),
    [formData?.proofOfIdentityAttachments],
  );
  const initialLabReportsFiles = useMemo(
    () => convertToUploadFiles((formData?.laboratoryAndPathologicalAttachments as unknown[]) || []),
    [formData?.laboratoryAndPathologicalAttachments],
  );
  const initialOtherDocumentsFiles = useMemo(
    () => convertToUploadFiles((formData?.otherAttachments as unknown[]) || []),
    [formData?.otherAttachments],
  );

  // 当前文件列表 - 初始化为初始文件列表
  const [claimFormPartIIFiles, setClaimFormPartIIFiles] = useState<UploadFile[]>(initialClaimFormPartIIFiles);
  const [identityProofFiles, setIdentityProofFiles] = useState<UploadFile[]>(initialIdentityProofFiles);
  const [labReportsFiles, setLabReportsFiles] = useState<UploadFile[]>(initialLabReportsFiles);
  const [otherDocumentsFiles, setOtherDocumentsFiles] = useState<UploadFile[]>(initialOtherDocumentsFiles);

  // 当初始文件列表更新时同步（如接口数据异步加载完成），参考 EditClaimForm 中 methods.reset 的回填逻辑
  useEffect(() => {
    setClaimFormPartIIFiles(initialClaimFormPartIIFiles);
  }, [initialClaimFormPartIIFiles]);
  useEffect(() => {
    setIdentityProofFiles(initialIdentityProofFiles);
  }, [initialIdentityProofFiles]);
  useEffect(() => {
    setLabReportsFiles(initialLabReportsFiles);
  }, [initialLabReportsFiles]);
  useEffect(() => {
    setOtherDocumentsFiles(initialOtherDocumentsFiles);
  }, [initialOtherDocumentsFiles]);

  const { keycloak } = useKeycloakReady();
  const [isDone, setIsDone] = useState(false);

  // 拦截浏览器原生返回按钮：当 updateFiles 为 'update' | 'view' 时，返回到 ClaimEnquiryPage(tabs:1)
  useEffect(() => {
    if (isDone || (updateFiles !== 'update' && updateFiles !== 'view')) {
      return;
    }
    const handlePopState = () => {
      window.history.go(-1);
      navigate('/ClaimEnquiryPage', { state: { tabs: 1 }, replace: true });
      window.removeEventListener('popstate', handlePopState);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [updateFiles, isDone, navigate]);

  const didRewriteRef = useRef(false);
  useEffect(() => {
    if (!isDone || didRewriteRef.current) {
      return;
    }
    didRewriteRef.current = true;
    const currentPath = location.pathname + (location.search || '');
    const currentState = location.state;
    navigate('/ClaimEnquiryPage', { replace: true, state: { tabs: 1 } });
    navigate(currentPath, { state: currentState });
    // eslint-disable-next-line
  }, [isDone, navigate]);

  const { data: relationList, shouldFetch: shouldFetchRelation } = useRelationshiplist(
    'useRelationshiplist',
    keycloak.token ?? '',
    '',
  );

  // 只上传新添加的文件（有 originFileObj 的文件）
  const getNewFiles = (files: UploadFile[]) => {
    return files.filter((f) => f.originFileObj).map((f) => f.originFileObj as RcFile);
  };

  // 已存在的旧文件数量（服务端文件、未被删除），用于新上传文件序号接续
  const getExistingCount = (files: UploadFile[]) => {
    return files.filter((f) => !f.originFileObj && f.status !== 'removed').length;
  };

  // 判断是否有新上传的文件（本地新增、带 originFileObj 的文件）
  const hasNewUploadedFiles = () => {
    return [...claimFormPartIIFiles, ...identityProofFiles, ...labReportsFiles, ...otherDocumentsFiles].some(
      (f) => f.originFileObj,
    );
  };

  const {
    shouldFetch: shouldUpdateFetch,
    data: updateData,
    error: updateError,
  } = useUploadFilesForCI(
    'useUploadFilesForCI_' + randoms,
    keycloak.token || '',
    formData?.referenceNo || '',
    formData?.policyNumber || '',
    getNewFiles(claimFormPartIIFiles),
    getNewFiles(identityProofFiles),
    getNewFiles(labReportsFiles),
    getNewFiles(otherDocumentsFiles),
    {
      critical: getExistingCount(claimFormPartIIFiles),
      proof: getExistingCount(identityProofFiles),
      laboratory: getExistingCount(labReportsFiles),
      other: getExistingCount(otherDocumentsFiles),
    },
  );

  const [mes, setMes] = useState('');

  const handlePepFileChange = (files: UploadFile[]) => {
    console.log('handlePepFileChange', files);
    setPepAttachments(files);
  };

  // 获取关系列表
  useEffect(() => {
    shouldFetchRelation(true);
  }, [shouldFetchRelation]);

  // 上传成功后跳转成功页
  useEffect(() => {
    if (updateData) {
      console.log('文件上传成功', updateData);
      setIsDone(true);
    }
    if (updateError) {
      console.error('文件上传失败', updateError);
      shouldUpdateFetch(false);
      navigate('/ChangeRequestError', {
        replace: true,
      });
    }
  }, [updateData, updateError, shouldUpdateFetch, navigate]);

  const displayValue = (value: string | number | boolean | undefined | null, fallback = '-') => {
    if (!value && value !== false && value !== 0) return fallback;
    return value;
  };

  const getCausedByLabel = (value: string) => {
    const causedByMap: Record<string, string> = {
      Illness: t('life.submitClaims.illness'),
      Accident: t('life.submitClaims.accident'),
      疾病: t('life.submitClaims.illness'),
      意外: t('life.submitClaims.accident'),
    };
    return causedByMap[value] || value;
  };

  const getYesNoLabel = (value: string) => {
    const yesNoMap: Record<string, string> = {
      Yes: isEnglish ? 'Yes' : '是',
      No: isEnglish ? 'No' : '否',
      是: isEnglish ? 'Yes' : '是',
      否: isEnglish ? 'No' : '否',
    };
    return yesNoMap[value] || value;
  };

  const getRelationshipLabel = (value: string) => {
    const relationItem = relationList?.find((item) => item.relationshipCode === value);
    if (relationItem) {
      return isEnglish ? relationItem.relationshipDesc : relationItem.relationshipDescTc;
    }
    return value;
  };

  const getSettlementLabel = (value: string) => {
    const settlementMap: Record<string, string> = {
      Cheque: t('life.submitClaims.settlementCheque'),
      'Accumulate in Benefit Accumulation Account': t('life.submitClaims.settlementAccumulate'),
      'Telegraphic Transfer': t('life.submitClaims.settlementTelegraphic'),
    };
    return settlementMap[value] || value;
  };

  const getDeliveryLabel = (value: string) => {
    const deliveryMap: Record<string, string> = {
      'By Mail': t('life.submitClaims.deliveryByMail'),
      'Through insurance intermediary': t('life.submitClaims.deliveryByInsurance'),
    };
    return deliveryMap[value] || value;
  };

  const getFileCount = (attachments: unknown): number => {
    if (Array.isArray(attachments)) {
      return attachments.length;
    }
    if (attachments && typeof attachments === 'object' && 'fileList' in attachments) {
      return (attachments as { fileList: unknown[] }).fileList?.length || 0;
    }
    return 0;
  };
  if (isDone) {
    return <EnquirySuccessPage enquiryReference={formData.bpmReferenceNo ?? ''} />;
  }
  const hasUploadedFiles =
    getFileCount(formData?.criticalIllnessClaimFormAttachments) +
      getFileCount(formData?.proofOfIdentityAttachments) +
      getFileCount(formData?.laboratoryAndPathologicalAttachments) +
      getFileCount(formData?.otherAttachments) >
    0;

  return (
    <ClaimPageContainer className="Claim">
      <GreetingSection
        pageName={updateFiles === 'normal' ? t('life.submitClaims.cardTitle') : t('claimDetails.title')}
        showicon={false}
      />
      <Row justify="center" gutter={[16, { xs: 8, sm: 8, lg: 24 }]} className="ClaimContent claim-confirm-page">
        <div style={{ padding: '24px', maxWidth: '640px', width: '100%', borderRadius: '8px' }}>
          {/* Page Header */}
          <Row>
            <Col xs={24} md={24}>
              {updateFiles === 'normal' && (
                <div style={{ fontSize: '33px', fontFamily: 'Roboto400', color: '#6f7072', marginBottom: '10px' }}>
                  {/* {updateFiles === 'view'
                  ? t('life.submitClaims.viewDetails')
                  : updateFiles === 'update'
                  ? t('life.submitClaims.updateFiles')
                  : 
                  t('life.submitClaims.ConfirmChanges')} */}
                  {t('life.submitClaims.ConfirmChanges')}
                </div>
              )}
              {updateFiles === 'normal' && (
                <div style={{ fontSize: '17px', fontFamily: 'Roboto700', color: '#E9673D', marginBottom: '20px' }}>
                  {t('life.submitClaims.ConfirmText')}
                </div>
              )}
            </Col>
          </Row>

          {/* Basic Information */}
          <Row justify="center" gutter={[16, { xs: 8, sm: 8, lg: 24 }]} style={{ rowGap: 0 }}>
            {/* Policy Number */}
            {formData?.policyNumber && formData.policyNumber.length > 0 && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.policyNumber')}>
                  {formData.policyNumber.split(',').join(', ')}
                </ConfirmField>
              </Col>
            )}

            {/* Caused By */}
            {formData?.causedBy && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.Causedby')}>
                  {getCausedByLabel(formData.causedBy)}
                </ConfirmField>
              </Col>
            )}

            {/* Date of Accident */}
            {formData?.dateOfAccident && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.dateOfAccident')}>{formData.dateOfAccident}</ConfirmField>
              </Col>
            )}

            {/* Place */}
            {formData?.place && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.place')}>{displayValue(formData.place)}</ConfirmField>
              </Col>
            )}

            {/* Accident Details */}
            {formData?.accidentDetails && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.accidentDetails')}>
                  {displayValue(formData.accidentDetails)}
                </ConfirmField>
              </Col>
            )}

            {/* Date Symptoms First Appeared */}
            {formData?.dateOfSymptoms && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.dateSymptomsFirstAppeared')}>
                  {formData.dateOfSymptoms}
                </ConfirmField>
              </Col>
            )}

            {/* Symptoms Details */}
            {formData?.symptomsDetails && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.symptomsDetails')}>
                  {displayValue(formData.symptomsDetails)}
                </ConfirmField>
              </Col>
            )}

            {/* First Consultation Date */}
            {formData?.firstConsultationDate && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.firstConsultationDate')}>
                  {formData.firstConsultationDate}
                </ConfirmField>
              </Col>
            )}
            {/* nameOfHospital */}
            {formData?.nameOfHospital && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.nameOfHospital')}>{formData.nameOfHospital}</ConfirmField>
              </Col>
            )}
            {/* Consulted Other Hospitals */}
            {formData?.otherHospitalsFiveYearsFlag !== undefined && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.consultedOtherHospitals')}>
                  {getYesNoLabel(formData.otherHospitalsFiveYearsFlag)}
                </ConfirmField>
              </Col>
            )}

            {/* Hospital Records */}
            {formData?.hospitalList && formData.hospitalList.length > 0 && (
              <Col xs={24} md={24}>
                <ConfirmField>
                  <div>
                    {formData.hospitalList.map((record: HospitalItem, index: number) => (
                      <div key={index} style={{ marginBottom: index < formData.hospitalList.length - 1 ? '16px' : 0 }}>
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{ color: '#6f7072', fontSize: '17px' }}>
                            {t('life.submitClaims.nameOfHospitalPhysicianAndAddress')}:
                          </span>
                          <div style={{ fontSize: '20px', fontFamily: 'Roboto400', padding: '4px 0' }}>
                            {displayValue(record.nameOfHospital)}
                          </div>
                        </div>
                        <div>
                          <span style={{ color: '#6f7072', fontSize: '17px' }}>
                            {t('life.submitClaims.consultationDate')}:
                          </span>
                          <div style={{ fontSize: '20px', fontFamily: 'Roboto400', padding: '4px 0' }}>
                            {record.consultationDate}
                          </div>
                        </div>

                        {index < (formData.hospitalList?.length || 0) - 1 && (
                          <Divider style={{ margin: '12px 0', borderColor: '#cccccc' }} />
                        )}
                      </div>
                    ))}
                  </div>
                </ConfirmField>
              </Col>
            )}

            {/* Has Family Illness */}
            {formData?.familMemberSufferedFlag !== undefined && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.haveFamilyIllness')}>
                  {getYesNoLabel(formData.familMemberSufferedFlag)}
                </ConfirmField>
              </Col>
            )}
            {/* Family Illness Records */}
            {formData?.familMemberList && formData.familMemberList.length > 0 && (
              <Col xs={24} md={24}>
                <ConfirmField>
                  <div>
                    {formData.familMemberList.map((record: FamilyMemberItem, index: number) => (
                      <div
                        key={index}
                        style={{ marginBottom: index < (formData.familMemberList?.length ?? 0) - 1 ? '16px' : 0 }}
                      >
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{ color: '#6f7072', fontSize: '17px' }}>
                            {t('life.submitClaims.dateOfDiagnosis')}:
                          </span>
                          <div style={{ fontSize: '20px', fontFamily: 'Roboto400', padding: '4px 0' }}>
                            {record.dateOfDiagnosis}
                          </div>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{ color: '#6f7072', fontSize: '17px' }}>
                            {t('life.submitClaims.relationshipWithInsured')}:
                          </span>
                          <div style={{ fontSize: '20px', fontFamily: 'Roboto400', padding: '4px 0' }}>
                            {getRelationshipLabel(record.relationshipWithInsured || '')}
                          </div>
                        </div>
                        <div>
                          <span style={{ color: '#6f7072', fontSize: '17px' }}>
                            {t('life.submitClaims.natureOfIllness')}:
                          </span>
                          <div style={{ fontSize: '20px', fontFamily: 'Roboto400', padding: '4px 0' }}>
                            {displayValue(record.natureOfIllness)}
                          </div>
                        </div>
                        {index < (formData.familMemberList?.length || 0) - 1 && (
                          <Divider style={{ margin: '12px 0', borderColor: '#cccccc' }} />
                        )}
                      </div>
                    ))}
                  </div>
                </ConfirmField>
              </Col>
            )}
            {/* Has Past Medical History */}
            {formData?.insuredConsultedFlag !== undefined && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.pastMedicalHistoryQuestion')}>
                  {getYesNoLabel(formData.insuredConsultedFlag)}
                </ConfirmField>
              </Col>
            )}

            {/* Past Medical Records */}
            {formData?.otherHospitalsList && formData.otherHospitalsList.length > 0 && (
              <Col xs={24} md={24}>
                <ConfirmField>
                  <div>
                    {formData.otherHospitalsList.map((record: OtherHospitalItem, index: number) => (
                      <div
                        key={index}
                        style={{ marginBottom: index < (formData.otherHospitalsList?.length ?? 0) - 1 ? '16px' : 0 }}
                      >
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{ color: '#6f7072', fontSize: '17px' }}>
                            {t('life.submitClaims.nameOfHospitalPhysicianAndAddress')}:
                          </span>
                          <div style={{ fontSize: '20px', fontFamily: 'Roboto400', padding: '4px 0' }}>
                            {displayValue(record.nameOfHospital)}
                          </div>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{ color: '#6f7072', fontSize: '17px' }}>
                            {t('life.submitClaims.consultationDate')}:
                          </span>
                          <div style={{ fontSize: '20px', fontFamily: 'Roboto400', padding: '4px 0' }}>
                            {record.consultationDate}
                          </div>
                        </div>
                        <div>
                          <span style={{ color: '#6f7072', fontSize: '17px' }}>
                            {t('life.submitClaims.illnessDiagnosis')}:
                          </span>
                          <div style={{ fontSize: '20px', fontFamily: 'Roboto400', padding: '4px 0' }}>
                            {displayValue(record.illness)}
                          </div>
                        </div>
                        {index < (formData.otherHospitalsList?.length || 0) - 1 && (
                          <Divider style={{ margin: '12px 0', borderColor: '#cccccc' }} />
                        )}
                      </div>
                    ))}
                  </div>
                </ConfirmField>
              </Col>
            )}

            {/* Has Other Insurance Claims */}
            {formData?.otherClaimSubmitFlag !== undefined && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.otherInsuranceClaimQuestion')}>
                  {getYesNoLabel(formData.otherClaimSubmitFlag)}
                </ConfirmField>
              </Col>
            )}

            {/* Other Insurance Claims */}
            {formData?.otherClaimList && formData.otherClaimList.length > 0 && (
              <Col xs={24} md={24}>
                <ConfirmField>
                  <div>
                    {formData.otherClaimList.map((claim: OtherClaimItem, index: number) => (
                      <div
                        key={index}
                        style={{ marginBottom: index < (formData.otherClaimList?.length ?? 0) - 1 ? '16px' : 0 }}
                      >
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{ color: '#6f7072', fontSize: '17px' }}>
                            {t('life.submitClaims.nameOfInsuranceCompany')}:
                          </span>
                          <div style={{ fontSize: '20px', fontFamily: 'Roboto400', padding: '4px 0' }}>
                            {displayValue(claim.nameOfInsuranceCompany)}
                          </div>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{ color: '#6f7072', fontSize: '17px' }}>
                            {t('life.submitClaims.policyNumber')}:
                          </span>
                          <div style={{ fontSize: '20px', fontFamily: 'Roboto400', padding: '4px 0' }}>
                            {displayValue(claim.policyNumber)}
                          </div>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{ color: '#6f7072', fontSize: '17px' }}>
                            {t('life.submitClaims.sumInsured')}:
                          </span>
                          <div style={{ fontSize: '20px', fontFamily: 'Roboto400', padding: '4px 0' }}>
                            {claim.sumInsured?.toUpperCase()} {formatCurrency(claim.sumInsuredAmount)}
                          </div>
                        </div>
                        <div>
                          <span style={{ color: '#6f7072', fontSize: '17px' }}>
                            {t('life.submitClaims.claimStatus')}:
                          </span>
                          <div style={{ fontSize: '20px', fontFamily: 'Roboto400', padding: '4px 0' }}>
                            {displayValue(claim.claimStatus)}
                          </div>
                        </div>
                        {index < (formData.otherClaimList?.length || 0) - 1 && (
                          <Divider style={{ margin: '12px 0', borderColor: '#cccccc' }} />
                        )}
                      </div>
                    ))}
                  </div>
                </ConfirmField>
              </Col>
            )}
          </Row>

          {/* Documents Section */}
          <div ref={documentsSectionRef}>
            {updateFiles === 'update' ? (
              <DocumentsCardForCIConfirm
                setInvalid={setDocumentInvalidCI}
                onClaimFormPartIIChange={setClaimFormPartIIFiles}
                onIdentityProofChange={setIdentityProofFiles}
                onLabReportsChange={setLabReportsFiles}
                onOtherDocumentsChange={setOtherDocumentsFiles}
                initialClaimFormPartIIFiles={initialClaimFormPartIIFiles}
                initialIdentityProofFiles={initialIdentityProofFiles}
                initialLabReportsFiles={initialLabReportsFiles}
                initialOtherDocumentsFiles={initialOtherDocumentsFiles}
                token={keycloak.token || ''}
              />
            ) : (
              (updateFiles === 'normal' || updateFiles === 'view') &&
              hasUploadedFiles && (
                <Row justify="center" gutter={[16, { xs: 8, sm: 8, lg: 24 }]}>
                  <Col xs={24} md={24}>
                    <div className="speakheader" style={{ paddingLeft: 0, fontSize: '22px' }}>
                      {t('life.enquiry.Supplementary')}
                    </div>
                  </Col>

                  <DocumentDisplayField
                    label={t('life.submitClaims.claimFormPartII')}
                    attachments={formData?.criticalIllnessClaimFormAttachments}
                  />

                  <DocumentDisplayField
                    label={t('life.submitClaims.identityProof')}
                    attachments={formData?.proofOfIdentityAttachments}
                  />

                  <DocumentDisplayField
                    label={t('life.submitClaims.labReports')}
                    attachments={formData?.laboratoryAndPathologicalAttachments}
                  />

                  <DocumentDisplayField
                    label={t('life.submitClaims.otherDocuments')}
                    attachments={formData?.otherAttachments}
                  />
                </Row>
              )
            )}
          </div>

          {/* Additional Information */}
          {/* <Row justify="center" gutter={[16, { xs: 8, sm: 8, lg: 24 }]} style={{ rowGap: 0 }}> */}
          <>
            {/* Settlement Option */}
            {formData?.settlementOptions && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.settlementOptions')}>
                  {getSettlementLabel(formData.settlementOptions)}
                </ConfirmField>
              </Col>
            )}

            {/* Cheque Currency */}
            {formData?.chequeCurrency && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.chequeCurrency')}>
                  {isEnglish ? formData.chequeCurrencyDesc : formData.chequeCurrencyTc}
                </ConfirmField>
              </Col>
            )}

            {/* Cheque Delivery Option */}
            {formData?.chequeDeliveryOption && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.submitClaims.chequeDeliveryOption')}>
                  {getDeliveryLabel(formData.chequeDeliveryOption)}
                </ConfirmField>
              </Col>
            )}

            {/* PEP Declaration */}
            {formData?.isDeclaration && (
              <Col xs={24} md={24}>
                <ConfirmField label={t('life.beneficiaries.PEP').replace(/<[^>]*>/g, '')}>
                  {getYesNoLabel(formData.isDeclaration)}
                </ConfirmField>
              </Col>
            )}

            {/* PEP Details */}
            {formData?.isDeclaration === 'Yes' && (
              <>
                {formData?.pepName && (
                  <Col xs={24} md={24}>
                    <ConfirmField label={t('life.beneficiaries.pepName')}>
                      {displayValue(formData.pepName)}
                    </ConfirmField>
                  </Col>
                )}

                {formData?.pepPosition && (
                  <Col xs={24} md={24}>
                    <ConfirmField label={t('life.beneficiaries.pepPosition')}>
                      {displayValue(formData.pepPosition)}
                    </ConfirmField>
                  </Col>
                )}

                {formData?.pepRelevantPartyName && (
                  <Col xs={24} md={24}>
                    <ConfirmField label={t('life.beneficiaries.pepRelevant')}>
                      {displayValue(formData.pepRelevantPartyName)}
                    </ConfirmField>
                  </Col>
                )}

                {formData?.pepRelationship && (
                  <Col xs={24} md={24}>
                    <ConfirmField label={t('life.beneficiaries.pepRelationship')}>
                      {displayValue(formData.pepRelationship)}
                    </ConfirmField>
                  </Col>
                )}
              </>
            )}
          </>
          {updateFiles === 'normal' && formData?.isDeclaration === 'Yes' && (
            <DocumentsCardPep setInvalid={setDocumentInvalidPep} onFileChange={handlePepFileChange} />
          )}
          {/* 展示 pep 文件 */}
          {updateFiles === 'view' && (
            <Row justify="center" gutter={[16, { xs: 8, sm: 8, lg: 24 }]}>
              {formData?.isDeclaration === 'Yes' &&
                formData?.pepAttachments &&
                (formData.pepAttachments as unknown[])?.length > 0 && (
                  <DocumentDisplayField
                    // label={t('life.submitClaims.pepAttachments')}
                    label={t('life.enquiry.Supplementary')}
                    attachments={formData?.pepAttachments}
                  />
                )}
            </Row>
          )}
          {/* 提示：没有新上传的文件 */}
          {mes && (
            <div style={{ marginTop: '20px' }}>
              <Alert message={t(mes)} type="error" className="error-forgot" style={{ marginBottom: 0 }} />
            </div>
          )}

          {/* Buttons */}

          <Row justify="center">
            <Col xs={24} md={24}>
              <div
                className="confrimbutton"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '16px',
                  paddingTop: 16,
                  maxWidth: '640px',
                  margin: '0 auto',
                }}
              >
                {updateFiles !== 'view' && (
                  <Button
                    disabled={
                      (updateFiles == 'normal' && documentInvalidPep) || (updateFiles == 'update' && documentInvalidCI)
                    }
                    onClick={() => {
                      // Submit logic
                      if (updateFiles == 'normal')
                        navigate('/CIOtpVerificationPage', {
                          state: { allData: { ...formData, pepAttachments } },
                        });
                      else {
                        if (hasNewUploadedFiles()) {
                          setMes('');
                          setRandoms(Math.random());
                          shouldUpdateFetch(true);
                        } else {
                          // 没有新上传的文件，展示提示
                          setMes('life.MakeRequest.hasnochange');
                        }
                      }
                    }}
                    type="primary"
                    className="subbutton"
                    style={{ cursor: 'pointer', flex: 1 }}
                  >
                    {updateFiles === 'update' ? t('common.submit') : t('life.MakeRequest.contact.Confirm')}
                  </Button>
                )}
                <Button
                  onClick={() => {
                    if (updateFiles === 'normal') {
                      navigate(-1);
                    } else {
                      navigate('/ClaimEnquiryPage', { state: { tabs: 1 }, replace: true });
                    }
                  }}
                  className="cancel"
                  style={{
                    cursor: 'pointer',
                    ...(updateFiles === 'view' ? { display: 'block', margin: '0 auto' } : { flex: 1, height: 56 }),
                  }}
                >
                  {t('life.MakeRequest.contact.Back')}
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </Row>
    </ClaimPageContainer>
  );
};
